import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { DateTime } from '~/components';
import { colors, weights } from '~/styles';
import { dateFormats } from '~/utils';
import { useConversation } from '../../ConversationContext';

// Renderer for the track-time-suggestions tool: a clean, pickable list (not a table). Rows are grouped by
// source and stay clickable on ANY turn, not just the latest — a pick is self-contained (absolute date +
// resolved references) and posts back into the normal preflight → track flow, which re-validates it from
// scratch (nothing is logged on click; a re-picked entry gets the duplicate warning). Only the three
// pickable sources render; "tracked" rows (already-logged time) are omitted — the model surfaces those
// in prose.
const GROUPS = [
  { source: 'recent', label: 'Recent' },
  { source: 'allocation', label: 'Allocated' },
  { source: 'available', label: 'Available' },
];

const S = {
  Container: styled.div`
    margin: 0.75rem 0;
  `,

  Group: styled.div`
    & + & {
      margin-top: 1rem;
    }
  `,

  GroupLabel: styled.div`
    font-size: 0.625rem;
    font-weight: ${weights.black};
    letter-spacing: 0.0625rem;
    text-transform: uppercase;
    color: ${colors.grey40};
    margin-bottom: 0.25rem;
  `,

  Item: styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.5rem 0.5rem;
    border-radius: 0.375rem;
    border-bottom: 1px solid ${colors.grey10};

    &:last-child {
      border-bottom: none;
    }

    ${({ $clickable }) =>
      $clickable &&
      `
      cursor: pointer;
      &:hover {
        background: ${colors.grey5};
      }
    `}
  `,

  Body: styled.div`
    min-width: 0;
  `,

  Subject: styled.div`
    font-size: 0.8125rem;
    font-weight: ${weights.bold};
    color: ${colors.black};
  `,

  Meta: styled.div`
    font-size: 0.75rem;
    color: ${colors.grey55};
    margin-top: 0.125rem;
  `,

  Amount: styled.div`
    flex-shrink: 0;
    text-align: right;
    white-space: nowrap;
  `,

  Hours: styled.div`
    font-size: 0.8125rem;
    font-weight: ${weights.bold};
    color: ${colors.black};
  `,

  Date: styled.div`
    font-size: 0.6875rem;
    color: ${colors.grey40};
    margin-top: 0.125rem;
  `,
};

// The metadata line under the subject: client, then role / task when present.
function metaLine(row) {
  const parts = [];
  if (row.client) parts.push(row.client);
  if (row.role) parts.push(row.role);
  if (row.task) parts.push(row.task);
  return parts.join(' · ');
}

// A picked suggestion sends two things through onSend: this plain-language log request as the visible chat
// message, and the row's resolved references attached invisibly via `hiddenContext` (see pickData). The
// text is for the user; the model works from the exact UUIDs, so a project sharing its name with another
// or a role with an inactive name-twin can't derail resolution. Hours are omitted for an `available`
// target (it carries none), so the model asks; an `endDate` turns it into a range the model fills across
// every working day in the span.
function pickMessage(row) {
  const when =
    row.endDate && row.endDate !== row.date
      ? `each working day from ${moment(row.date).format('MMM D')} through ${moment(row.endDate).format(dateFormats.shortDate)}`
      : `on ${moment(row.date).format(dateFormats.shortDate)}`;

  if (row.type === 'time_off') {
    const lead = row.hours != null ? `Log ${row.hours}h of ${row.subject}` : `Log ${row.subject} time`;
    return `${lead} ${when}.`;
  }

  const target = row.client ? `${row.subject} (${row.client})` : row.subject;
  const roleTask = [row.role && `${row.role} role`, row.task && `${row.task} task`].filter(Boolean).join(', ');
  const detail = roleTask ? ` — ${roleTask} —` : '';
  const lead = row.hours != null ? `Log ${row.hours}h to ${target}` : `Log time to ${target}`;
  return `${lead}${detail} ${when}.`;
}

// The machine half of a pick: the row's resolved UUIDs, dates, and hours, JSON-stringified into the
// message's hiddenContext so preflight receives exact references — never re-resolved from the names in
// the visible text.
function pickData(row) {
  return JSON.stringify(
    Object.fromEntries(
      Object.entries({
        type: row.type,
        date: row.date,
        endDate: row.endDate,
        hours: row.hours,
        projectId: row.projectId,
        projectRoleId: row.projectRoleId,
        projectTaskId: row.projectTaskId,
        timeOffTypeId: row.timeOffTypeId,
      }).filter(([, value]) => value != null),
    ),
  );
}

export default function TrackTimeSuggestions({ data }) {
  const conversation = useConversation();
  const onSend = conversation?.onSend;

  if (!Array.isArray(data) || data.length === 0) return null;

  // Not gated on the latest turn (mid-stream clicks are already inert — handleSend no-ops while
  // thinking). The confirmation card's action IS latest-turn only: an approval is context-bound,
  // a pick is re-validated.
  const clickable = typeof onSend === 'function';
  const groups = GROUPS.map((group) => ({
    ...group,
    rows: data.filter((row) => row.source === group.source),
  })).filter((group) => group.rows.length > 0);

  return (
    <S.Container>
      {groups.map((group) => (
        <S.Group key={group.source}>
          <S.GroupLabel>{group.label}</S.GroupLabel>
          {group.rows.map((row, index) => {
            const meta = metaLine(row);
            return (
              <S.Item
                key={index}
                $clickable={clickable}
                onClick={clickable ? () => onSend(pickMessage(row), { hiddenContext: pickData(row) }) : undefined}>
                <S.Body>
                  <S.Subject>{row.subject}</S.Subject>
                  {meta && <S.Meta>{meta}</S.Meta>}
                </S.Body>
                {(row.hours != null || row.date) && (
                  <S.Amount>
                    {row.hours != null && <S.Hours>{row.hours}h</S.Hours>}
                    {row.date && (
                      <S.Date>
                        <DateTime value={row.date} format={dateFormats.compactDate} />
                        {row.endDate && (
                          <>
                            {' – '}
                            <DateTime value={row.endDate} format={dateFormats.compactDate} />
                          </>
                        )}
                      </S.Date>
                    )}
                  </S.Amount>
                )}
              </S.Item>
            );
          })}
        </S.Group>
      ))}
    </S.Container>
  );
}
